import React, { useState, useEffect, useCallback } from "react";
import { Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import { HiChevronDown, HiChevronUp, HiXMark, HiStar } from "react-icons/hi2";
import { FaHotel, FaMapMarkerAlt, FaFilter, FaCheckCircle } from "react-icons/fa";
import { baseurl } from "../../baseurl";
import "../../asset/css/hotel-results.css";
import { BiArrowToRight } from "react-icons/bi";

const HotelResult = () => {
  const [searchParams] = useSearchParams();
  const [hotels, setHotels] = useState([]);
  const [filteredHotels, setFilteredHotels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedId, setExpandedId] = useState(null);
  const [showMobileFilters, setShowMobileFilters] = useState(false);

  // --- MARKUP STATE ---
  const [hotelMarkup, setHotelMarkup] = useState({ type: "flat", amount: 0 });

  // Filter States
  const [maxPrice, setMaxPrice] = useState(10000);
  const [starFilter, setStarFilter] = useState("all");

  // --- MARKUP CALCULATION LOGIC ---
  const calculateFinalFare = useCallback((baseFare, hotelName = "Hotel") => {
    const fare = parseFloat(baseFare) || 0;
    const markupAmt = parseFloat(hotelMarkup.amount) || 0;
    const type = hotelMarkup.type?.toLowerCase(); 
    
    let finalFare = 0;
    let calculationLog = "";

    if (type === "percent" || type === "percentage") {
      const addedAmount = fare * (markupAmt / 100);
      finalFare = fare + addedAmount;
      calculationLog = `Base: ${fare} + (${markupAmt}% of ${fare} = ${addedAmount})`;
    } else {
      // Default to 'flat'
      finalFare = fare + markupAmt;
      calculationLog = `Base: ${fare} + Flat Markup: ${markupAmt}`;
    }

    const roundedFare = Math.round(finalFare);

    // Detailed Console Log for Debugging
    console.group(`Fare Calculation: ${hotelName}`);
    console.log(`Base Fare: ₹${fare}`);
    console.log(`Markup: ${markupAmt} (${type})`);
    console.log(`Calculation: ${calculationLog}`);
    console.log(`Final Rounded Result: ₹${roundedFare}`);
    console.groupEnd();
    
    return roundedFare;
  }, [hotelMarkup]);

  // --- FETCH MARKUP & HOTELS ---
  useEffect(() => {
    const fetchInitialData = async () => {
      setLoading(true);
      
      // 1. Fetch Markup Configuration (Service ID 1 for Hotels)
      try {
        const markupRes = await axios.post(`${baseurl}/getServiceMarkupsGuest`);
        console.log("Markup API Full Response:", markupRes.data);

        // Targeted extraction based on your response structure
        const markupData = markupRes.data?.markups || [];

        if (Array.isArray(markupData)) {
          const hotelConfig = markupData.find(m => String(m.service_id) === "1");
          if (hotelConfig) {
            console.log("Found Hotel Markup Config:", hotelConfig);
            setHotelMarkup({
              type: hotelConfig.markup_type,
              amount: hotelConfig.amount
            });
          }
        }
      } catch (err) {
        console.error("Error fetching hotel markup:", err);
      }

      // 2. Fetch Hotels
      const formatDateForAPI = (dateStr) => {
        if (!dateStr) return "";
        const [year, month, day] = dateStr.split("-");
        return `${day}-${month}-${year}`;
      };

      try {
        const payload = {
          checkin: formatDateForAPI(searchParams.get("checkIn")),
          checkout: formatDateForAPI(searchParams.get("checkOut")),
          city_code: searchParams.get("cityCode"),
          rooms: searchParams.get("rooms"),
          adults: searchParams.get("adults"),
          infants: searchParams.get("infants"),
          children: searchParams.get("children"),
          userId: "1"
        };
        
        const response = await axios.get(`${baseurl}/searchHotels`, { params: payload });
        const results = response.data.apiResponse?.Search?.HotelSearchResult?.HotelResults || [];
        setHotels(results);
        setFilteredHotels(results);
      } catch (err) {
        console.error("Hotel API Error:", err);
      } finally {
        setLoading(false);
      }
    };

    if (searchParams.get("cityCode")) fetchInitialData();
  }, [searchParams]);

  // --- FILTER LOGIC ---
  useEffect(() => {
    let temp = hotels.filter((h) => {
      const finalPrice = calculateFinalFare(h.Price?.PublishedPriceRoundedOff || 0);
      const matchesPrice = finalPrice <= maxPrice;
      const matchesStar = starFilter === "all" || String(h.StarRating) === String(starFilter);
      return matchesPrice && matchesStar;
    });
    setFilteredHotels(temp);
  }, [maxPrice, starFilter, hotels, calculateFinalFare]);

  const FilterContent = () => (
    <div className="filter-card p-3 p-lg-4 shadow-sm bg-white border-0 rounded-4">
      <div className="d-flex align-items-center mb-4">
        <FaFilter className="me-2 text-primary" />
        <h5 className="fw-bold m-0">Filters</h5>
        <button className="btn btn-link btn-sm ms-auto text-decoration-none p-0 text-danger" 
          onClick={() => { setMaxPrice(10000); setStarFilter('all'); }}>
          Reset
        </button>
      </div>

      <div className="filter-section mb-4 pb-3 border-bottom">
        <label className="form-label small fw-bold text-uppercase text-muted">Max Price: ₹{maxPrice}</label>
        <input type="range" className="form-range" min="500" max="25000" step="500" 
          value={maxPrice} onChange={(e) => setMaxPrice(e.target.value)} />
      </div>

      <div className="filter-section">
        <label className="form-label small fw-bold text-uppercase text-muted mb-3 d-block">Star Rating</label>
        {['all', '5', '4', '3', '2', '1'].map(star => (
          <div className="form-check mb-2" key={star}>
            <input className="form-check-input" type="radio" name="star" id={`star-${star}`} 
              checked={starFilter === star} onChange={() => setStarFilter(star)} />
            <label className="form-check-label small" htmlFor={`star-${star}`}>
              {star === 'all' ? 'All Ratings' : `${star} Star`}
            </label>
          </div>
        ))}
      </div>
    </div>
  );

  if (loading) return (
    <div className="hotel-loader-container text-center py-5 mt-5">
      <div className="spinner-border text-primary mb-3" role="status"></div>
      <p className="fw-bold text-muted">Finding the best hotels in {searchParams.get("cityName")}...</p>
    </div>
  );

  return (
    <section className="hotel-results-bg py-3 py-lg-4 bg-light min-vh-100" style={{marginTop: "100px"}}>
      <div className="container">
        {/* Mobile Header */}
        <div className="d-lg-none sticky-top bg-white p-2 shadow-sm rounded-3 mb-3 border" style={{ top: "10px", zIndex: 1020 }}>
          <div className="d-flex justify-content-between align-items-center">
            <div className="small px-2">
              <span className="fw-bold">{filteredHotels.length}</span> Hotels Found
            </div>
            <button className="btn btn-primary btn-sm px-3 fw-bold shadow-sm" onClick={() => setShowMobileFilters(true)}>
              <FaFilter className="me-1" size={12} /> Filter
            </button>
          </div>
        </div>

        <div className="row g-4">
          <div className="col-lg-3 d-none d-lg-block">
            <div className="sticky-top" style={{ top: "75px" }}>

              

<style>
  {`
  .route-indicator {
  display: flex;
  align-items: center;
  gap: 12px;
  background: #f8f9fa; /* Light subtle background */
  padding: 8px 16px;
  border-radius: 8px;
  width: fit-content;
  border: 1px solid #e0e0e0;
  margin-bottom: 15px;
}

.location-text {
  font-size: 1rem;
  font-weight: 600;
  color: #2c3e50;
  text-transform: capitalize;
}

.route-icon {
  color: #3498db; /* Blue accent for the arrow */
  font-size: 1.2rem;
  display: flex;
  align-items: center;
}

/* Optional: Make it look better on mobile */
@media (max-width: 480px) {
  .route-indicator {
    gap: 8px;
    padding: 6px 12px;
  }
  .location-text {
    font-size: 0.9rem;
  }
}
`}
</style>

              <div className="route-indicator">
  <span className="location-text">Hotels in {searchParams.get("cityName")}</span> 
</div>



              <FilterContent />
            </div>
          </div>

          <div className="col-lg-9">
            <div className="d-none d-lg-flex justify-content-between align-items-center mb-3">
              <h5 className="fw-bold m-0 text-dark">Hotels in {searchParams.get("cityName")}</h5>
              <div className="small text-muted bg-white border px-3 py-1 rounded-pill shadow-sm">
                {searchParams.get("checkIn")} — {searchParams.get("checkOut")}
              </div>
            </div>

            {filteredHotels.length === 0 ? (
              <div className="text-center p-5 bg-white rounded-4 shadow-sm mt-4">
                <p className="text-muted">No hotels found matching your criteria.</p>
              </div>
            ) : (
              filteredHotels.map((hotel) => {
                const finalPrice = calculateFinalFare(hotel.Price.PublishedPriceRoundedOff, hotel.HotelName);
                
                return (
                  <div className="hotel-card-wrapper mb-3" key={hotel.HotelCode}>
                    <div className="hotel-card shadow-sm border-0 bg-white rounded-3 overflow-hidden">
                      <div className="row g-0">
                        <div className="col-12 col-md-4 position-relative">
                          <img src={hotel.HotelPicture} alt={hotel.HotelName} className="hotel-img w-100 h-100" style={{ objectFit: "cover", minHeight: "200px" }}
                            onError={(e) => { e.target.src = "https://via.placeholder.com/300x200?text=Hotel+Image"; }}
                          />
                        </div>

                        <div className="col-12 col-md-5 p-3">
                          <div className="d-flex align-items-center mb-1">
                            {[...Array(parseInt(hotel.StarRating))].map((_, i) => (
                              <HiStar key={i} className="text-warning me-1" />
                            ))}
                          </div>
                          <h5 className="fw-bold text-dark mb-1">{hotel.HotelName}</h5>
                          <p className="small text-muted mb-2"><FaMapMarkerAlt className="me-1" /> {hotel.HotelLocation}</p>
                        </div>

                        <div className="col-12 col-md-3 p-3 bg-light-subtle text-center border-md-start">
                          <div className="h-100 d-flex flex-column justify-content-center">
                            <div className="h3 fw-bold text-primary mb-0">₹{finalPrice}</div>
                            <p className="x-small text-muted mb-3">+ ₹{hotel.Price.Tax} taxes & fees</p>

                            <Link to={`/hotel-detail/${hotel.ResultToken}?adult=${searchParams.get("adults")}&children=${searchParams.get("children")}&infants=${searchParams.get("infants")}`}
                              className="btn btn-primary w-100 fw-bold py-2 mb-2">
                              BOOK NOW
                            </Link>

                            <div className="x-small text-primary cursor-pointer fw-bold"
                              onClick={() => setExpandedId(expandedId === hotel.HotelCode ? null : hotel.HotelCode)}>
                              {expandedId === hotel.HotelCode ? <><HiChevronUp /> HIDE DETAILS</> : <><HiChevronDown /> VIEW DETAILS</>}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    {expandedId === hotel.HotelCode && (
                      <div className="details-box p-3 p-md-4 bg-white border border-top-0 animate-fade-in shadow-sm rounded-bottom-3">
                        <div className="row">
                          <div className="col-md-7">
                            <h6 className="fw-bold mb-3 small text-uppercase">Description & Address</h6>
                            <p className="small text-muted mb-3">{hotel.HotelAddress}</p>
                            <h6 className="fw-bold mb-3 small text-uppercase">Main Amenities</h6>
                            <div className="row g-2">
                              {hotel.HotelAmenities.map((amt, i) => (
                                <div key={i} className="col-6 x-small text-capitalize"><FaCheckCircle className="text-success me-2" /> {amt}</div>
                              ))}
                            </div>
                          </div>
                          <div className="col-md-5 border-start ps-md-4 mt-3 mt-md-0">
                            <h6 className="fw-bold mb-3 small text-uppercase">Stay Information</h6>
                            <div className="bg-light p-3 rounded-3 x-small">
                              <div className="d-flex justify-content-between mb-2">
                                <span>Currency:</span><span className="fw-bold">{hotel.Price.CurrencyCode}</span>
                              </div>
                              <div className="d-flex justify-content-between mb-2">
                                <span>Rating:</span><span className="fw-bold">{hotel.StarRating} Star</span>
                              </div>
                              <hr />
                              <div className="fw-bold text-dark">Price Summary</div>
                              <div className="d-flex justify-content-between mt-2">
                                <span>Room Price (Inc. Markup):</span><span>₹{finalPrice}</span>
                              </div>
                              <div className="d-flex justify-content-between">
                                <span>Tax:</span><span>₹{hotel.Price.Tax}</span>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })
            )}
          </div>
        </div>
      </div>

      {showMobileFilters && (
        <div className="position-fixed top-0 start-0 w-100 h-100 bg-white" style={{ zIndex: 2000, overflowY: "auto" }}>
          <div className="p-3 border-bottom d-flex justify-content-between align-items-center">
            <h5 className="fw-bold m-0">Filters</h5>
            <HiXMark size={24} onClick={() => setShowMobileFilters(false)} />
          </div>
          <div className="p-3">
            <FilterContent />
            <button className="btn btn-primary w-100 py-3 mt-4 fw-bold" onClick={() => setShowMobileFilters(false)}>APPLY</button>
          </div>
        </div>
      )}
    </section>
  );
};

export default HotelResult;